import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AppInitService } from 'src/app/app-init.service';
import { AppConfigurations } from 'src/app/shared/models/appConfiguration.model';
import { ChecklistRequestModel } from '../models/checklistRequestModel';
import { ChecklistResponseModel } from '../models/checklistResponseModel';

const CREATE_ACTION = 'create';
const UPDATE_ACTION = 'update';
declare const window: any;

@Injectable({
  providedIn: 'root'
})
export class ChecklistService {
  private serviceId: number = 0;
  private serviceName: string = '';
  private isEdit: boolean = false;
  private baseUrl: string = '';
  private apiController: string = '';
  private appConfig!: AppConfigurations;

  constructor(private http: HttpClient, private appInitService: AppInitService) {
    // DELETE THIS: this.appConfig = this.appInitService.getValue();

    // Not unsubscribing because this service should not be destroyed
    this.appInitService.getAppConfiguration$()
    .subscribe({
      next: (config) => this._onReceiveConfig(config)
    });
  }

  private _onReceiveConfig(appConfig: AppConfigurations) {
    this.appConfig = appConfig;
    this.baseUrl = this.appConfig.adminApi.baseApiUrl;
    this.apiController = this.appConfig.adminApi.checklistController; 
  }

  public data: any[] = [];

  public setServiceId(serviceId: number) {
    this.serviceId = serviceId;
  }

  public setServiceName(serviceName: string) {
    this.serviceName = serviceName;
  }

  public setIsEdit(isEdit: boolean) {
    this.isEdit = isEdit;
  }

  public getIsEdit() {
    return this.isEdit;
  }

  public getServiceName(): string {
    return this.serviceName;
  }

  public read(): Observable<ChecklistResponseModel[]> {
    return this.fetch()
      .pipe(
        tap(data => {
          const sortedData = [...data].sort((a, b) => (a.sortValue > b.sortValue) ? 1 : -1);
          this.data = sortedData;
        })
      );
  }

  public save(data: ChecklistRequestModel, isNew?: boolean): Observable<any[]> {
    const action = isNew ? CREATE_ACTION : UPDATE_ACTION;
    const sortValue = isNew ? this.data.length + 1 : data.sortValue;

    this.reset();

    data.serviceId = this.serviceId;
    data.sortValue = sortValue;
    
    return this.saveChecklist(action, data);
  }

  public remove(checklistId: number): Observable<any[]> {
    this.reset();

    return this.removeChecklist(checklistId);
  }

  public removeChecklistForService(serviceId: number): Observable<any> {
    return this.http.delete<any[]>(`${this.baseUrl}${this.apiController}/forservice/${serviceId}`);
  }

  public rearrange(data: ChecklistRequestModel[]): Observable<any[]> {
    
    data = data.map(i => {
      return {
        sortValue: i.sortValue,
        checklistItem: i.checklistItem,
        linkName: i.linkName,
        link: encodeURIComponent(i.link),
        checklistId: i.checklistId,
        serviceId: i.serviceId
      }
    })
    
    data.forEach(element => {
      element.serviceId = this.serviceId;
    });
    return this.http
        .patch<any[]>(`${this.baseUrl}${this.apiController}/reorder`, data);
  }

  public resetItem(dataItem: ChecklistResponseModel) {
      if (!dataItem || dataItem.checklistId === 0) { return; }

      // find orignal data item
      const originalDataItem = this.data.find(item => item.checklistId === dataItem.checklistId);

      // revert changes
      Object.assign(originalDataItem, dataItem);
  }

  private reset() {
      this.data = [];
  }

  private fetch(): Observable<ChecklistResponseModel[]> {
    return this.http
        .get<ChecklistResponseModel[]>(`${this.baseUrl}${this.apiController}/checklists/${this.serviceId}`)
        .pipe(map(chklists => chklists.map(data => {
          return {
            ...data,
            link: decodeURIComponent(data.link)
          }
        })));
  }

  private saveChecklist(action: string = '', data: ChecklistRequestModel): Observable<any[]> {

    data.link = encodeURIComponent(data.link);

    switch (action) {
      case (CREATE_ACTION):
        return this.http.post<any[]>(`${this.baseUrl}${this.apiController}`, data);

      case (UPDATE_ACTION):
        return this.http.put<any[]>(`${this.baseUrl}${this.apiController}`, data);

      default:
        break;
    }

    throw new Error('Unexpected code path reached!');
  }

  private removeChecklist(checklistId: number): Observable<any[]> {
    return this.http.delete<any[]>(`${this.baseUrl}${this.apiController}/${checklistId}`);
  }

  private serializeModels(data?: any): string {
      return data ? `&models=${JSON.stringify([data])}` : '';
  }
}
